/** Iter 47 — screen walkthrough helpers. Pure, no React. */

export const ORDER_WORDING = "Observed source order, not guaranteed runtime order.";
export const WALK_ROLES = ["input/output", "input", "output", "display"];
export const WALK_MAPPINGS = ["proven", "ambiguous", "screen_only"];

export const ROLE_META = {
  "input/output": { label: "input / output", bg: "#e5f0ff", fg: "#0043ce", border: "#78a9ff", hint: "Both input-capable and displayed back (DSPF usage B, or separate input + output rows)." },
  input: { label: "input", bg: "#e5f0ff", fg: "#0043ce", border: "#78a9ff", hint: "Input-capable screen field — the user can type into it (DSPF usage I)." },
  output: { label: "output", bg: "#e8daff", fg: "#6929c4", border: "#be95ff", hint: "Output/display field shown to the user (DSPF usage O, M or blank)." },
  display: { label: "display", bg: "#fff0f7", fg: "#9f1853", border: "#ffafd2", hint: "Hidden or program-to-system field (DSPF usage H / P) — in the record buffer, not shown as data." },
};

export const MAPPING_META = {
  proven: { label: "proven", fg: "#0e6027", hint: "Database mapping proven by DDS REF / REFFLD." },
  ambiguous: { label: "ambiguous", fg: "#8a6d00", hint: "Same-name match only — a possible database field, never claimed as lineage." },
  screen_only: { label: "screen-only", fg: "#525252", hint: "No database mapping claimed for this screen field." },
};

export const roleMeta = (role) => ROLE_META[role] || ROLE_META.display;
export const mappingMeta = (mapping) => MAPPING_META[mapping] || MAPPING_META.screen_only;
export const isInputCapable = (field) => Boolean(field?.input_capable || field?.role === "input" || field?.role === "input/output");

/** Split a step's fields into the two tables the page renders. */
export function splitFields(fields) {
  const input = [];
  const output = [];
  for (const f of fields || []) (isInputCapable(f) ? input : output).push(f);
  return { input, output };
}

/** "CUSTDSP/CUSTCTL" · "SHARED · ambiguous format name" · "NOSUCH · unresolved format usage" */
export function stepTarget(step) {
  if (!step) return "";
  if (step.status === "bound" && step.display_file) return `${step.display_file}/${step.record_format}`;
  if (step.status === "ambiguous") return `${step.record_format} · ambiguous format name`;
  return `${step.record_format} · unresolved format usage`;
}

/** Database column wording: "PRODLIB/CUSTMAST.CUSTNO" · "possible — not proven" · "screen-only". */
export function mappingText(field) {
  if (!field) return "";
  if (field.mapping === "proven") return `${field.file_library ? `${field.file_library}/` : ""}${field.file_name}${field.field_id ? "" : " (not in DSPFFD)"}`;
  if (field.mapping === "ambiguous") return "possible — not proven";
  return "screen-only";
}

/** "12 steps · 9 bound · 2 ambiguous · 1 unresolved" */
export function walkthroughSummaryLine(s) {
  if (!s || !s.steps) return "";
  const st = s.by_status || {};
  return `${s.steps} screen statement${s.steps === 1 ? "" : "s"} · ${st.bound || 0} bound · ${st.ambiguous || 0} ambiguous format name${(st.ambiguous || 0) === 1 ? "" : "s"} · ${st.unresolved || 0} unresolved format usage${(st.unresolved || 0) === 1 ? "" : "s"}`;
}

/** Program picker filter — case-insensitive on program, library and display files. */
export function filterPrograms(programs, q) {
  const needle = (q || "").trim().toUpperCase();
  if (!needle) return programs || [];
  return (programs || []).filter((p) =>
    (p.program_name || "").includes(needle) || (p.program_library || "").includes(needle) || (p.display_files || []).some((d) => d.includes(needle)));
}
